import { Navigate, useLocation, useRoutes } from "react-router-dom";
import { lazy, useEffect, useState } from "react";
import SuspenseFn from "../shared/suspense/Suspense.jsx";
import { PageNotFound } from "../components/not-found/Not-found.jsx";
import AuthGuard from "../core/auth-guard/Auth-guard.js";
import { Login } from "../pages/auth/login/Login.jsx";
import Register from "../pages/auth/register/Register.jsx";
import PermissonGuard from "../core/permission-guard/Permission-guard.js";
import { AccessDenied } from "../components/access-denied/Access-denied.jsx";
import SyncFloorRoom from "../pages/roomSetting/syncFloorRoom.jsx";
import HotelGuard from "../core/hotel-guard/Hotel-guard.js";
import ReceptionDashboard from "../pages/dashboard/ReceptionDashboard";
import AssetReport from "../pages/assetmanagement/AssetReportTable.jsx";
import { useSelector } from "react-redux";
import { selectAuthData } from "../core/store/redux/slices/Auth-slice.js";
import {
  selectPermissions,
  selectPermissionsError,
} from "../core/store/redux/slices/Permission-slice.js";
import { resolveLandingRoute, isLandingRouteResolving } from "../core/permissions";
import { useMediaQuery } from "@mui/material";
import OfflineArrival from "../pages/arrival/OfflineArrival.jsx";
import useNetworkStatus from "../hooks/useNetworkStatus.js";
import { getDataFromDB } from "../db/dbHelpers.js";
import Employee from "../pages/Employee/Employee.jsx";
import CustomerSupport from "../pages/support/index.jsx";

const MainLayout = lazy(() => import("../pages/layout/Layout.jsx"));
const GuestInfo = lazy(() => import("../pages/guestinfo/GuestInfo.jsx"));
const RoomSetting = lazy(() => import("../pages/roomSetting/roomSetting.jsx"));
const RoomTypeForm = lazy(() =>
  import("../pages/roomSetting/roomTypeForm.jsx")
);
const Reservation = lazy(() => import("../pages/reservation/Reservation.jsx"));
const NewReservation = lazy(() =>
  import("../pages/reservation/newReservation/NewReservation.jsx")
);
// const Employee= lazy(() => import("../pages/Employee/Employee.jsx"));
const Arrival = lazy(() => import("../pages/arrival/Arrival.jsx"));
const Departure = lazy(() => import("../pages/departure/Departure.jsx"));
// const AlertSetting = lazy(() =>
//   import("../pages/alertsetting/AlertSetting.jsx")
// );
const Notification = lazy(() =>
  import("../pages/notification/Notification.jsx")
);
const UpdateProfile = lazy(() =>
  import("../pages/updateprofile/UpdateProfile.jsx")
);
const HouseKeepingEmployees = lazy(() =>
  import("../pages/housekeeping/Employees.jsx")
);
const HouseKeepingTasks = lazy(() => import("../pages/housekeeping/Tasks.jsx"));
const ChargePosting = lazy(() =>
  import("../pages/chargePosting/ChargeTable.jsx")
);
const NewChargePosting = lazy(() =>
  import("../pages/chargePosting/NewChargePosting.jsx")
);
const GuestDisbursementTable = lazy(() =>
  import("../pages/occupancyCharge/GuestDisbursementTable.jsx")
);
const PaidOutTable = lazy(() =>
  import("../pages/occupancyCharge/PaidOutTable.jsx")
);
const Cctv = lazy(() => import("../pages/cctv/Cctv.jsx"));
const SalesReports = lazy(() => import("../pages/report/SalesReport.jsx"));
const CollectionReports = lazy(() =>
  import("../pages/report/CollectionReport.jsx")
);
const GstReports = lazy(() => import("../pages/report/GstReport.jsx"));
const GuestReport = lazy(() => import("../pages/report/GuestReport.jsx"));
const ForeignerReports = lazy(() =>
  import("../pages/report/ForeignerReport.jsx")
);
const OccupancyReports = lazy(() =>
  import("../pages/report/OccupancyReport.jsx")
);
const CancellationReports = lazy(() =>
  import("../pages/report/CancellationReport.jsx")
);
const HousekeepingReports = lazy(() =>
  import("../pages/report/HousekeepingReport.jsx")
);

const NightAuditReport = lazy(() =>
  import("../pages/report/NightAuditReport.jsx")
);
const NightAuditOperationalReport = lazy(() =>
  import("../pages/report/NightAuditOperationalReport.jsx")
);
const NightAuditProcess = lazy(() =>
  import("../pages/nightAudit/NightAuditProcess.jsx")
);

const AdminDashboard = lazy(() =>
  import("../pages/adminDashboard/AdminDashboard.jsx")
);
const HotelSetupStepper = lazy(() =>
  import("../pages/roomSetup/HotelSetupLanding.jsx")
);
const OfflineBanner = lazy(() => import("../pages/offline/OfflineBanner.jsx"));

const SelfCheckIn = lazy(() =>
  import("../pages/selfCheckIn/SelfCheckInLayout.jsx")
);

const SelfUpdate = lazy(() =>
  import("../pages/selfupdate/SelfUpdateLayout.jsx")
);
const DynamicPricing = lazy(() =>
  import("../pages/revenuemanagement/DynamicPricing.jsx")
);

const ChannelPerformance = lazy(() =>
  import("../pages/revenuemanagement/ChannelPerformance.jsx")
);

const AddRule = lazy(() => import("../pages/revenuemanagement/AddRule.jsx"));

const RateUpdate = lazy(() =>
  import("../pages/channelManagement/rateUpdate/RateUpdate.jsx")
);

const RatePlan = lazy(() =>
  import("../pages/ratePlan/RatePlan.jsx")
);

const BookingEngineConfig = lazy(() =>
  import("../pages/bookingEngineConfig/BookingEngineConfig.jsx")
);

const Restrictions = lazy(() =>
  import("../pages/channelManagement/restrictions/Restrictions.jsx")
);

const UpdateRooms = lazy(() =>
  import("../pages/channelManagement/roomUpdate/UpdateRooms.jsx")
);

const GmailAuth = lazy(() => import("../pages/auth/googleauth/GoogleAuth.jsx"));

const TutorialManagement = lazy(() => import("../pages/tutorial/TutorialManagement.jsx"));
const TutorialView = lazy(() => import("../pages/tutorial/TutorialView.jsx"));
const CouponsPage = lazy(() => import("../pages/coupons/CouponsPage.jsx"));
const GuestOffersPage = lazy(() => import("../pages/coupons/GuestOffersPage.jsx"));

const UserManagement = lazy(() =>
  import("../pages/userManagement/UserManagement.jsx")
);
const UserForm = lazy(() => import("../pages/userManagement/UserForm.jsx"));
const UserView = lazy(() => import("../pages/userManagement/UserView.jsx"));
const RoleManagement = lazy(() =>
  import("../pages/roleManagement/RoleManagement.jsx")
);
const RoleForm = lazy(() => import("../pages/roleManagement/RoleForm.jsx"));
const RoleView = lazy(() => import("../pages/roleManagement/RoleView.jsx"));
const AuditManagement = lazy(() => import("../pages/audit-management/AuditManagement.jsx"));
const TransactionTypes = lazy(() => import("../pages/approvalWorkflow/TransactionTypes.jsx"));
const ApprovalQueue = lazy(() => import("../pages/approvalWorkflow/ApprovalQueue.jsx"));
const SetPassword = lazy(() =>
  import("../pages/auth/setPassword/SetPassword.jsx")
);

function BaseRoutes() {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [toggle, setToggle] = useState(isMobile);
  const authData = useSelector(selectAuthData);
  const [offlineData, setOfflineData] = useState(null);
  const [offlineUserData, setOfflineUserData] = useState(null);
  const location = useLocation();
  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    if (!isOnline) {
      const fetchOfflineData = async () => {
        const data = await getDataFromDB("credentials");
        const userData = await getDataFromDB("user");
        if (data) {
          setOfflineData(data);
        }
        if (userData) {
          setOfflineUserData(userData);
        }
      };
      fetchOfflineData();
    }
  }, [isOnline]);

  const isAuthenticated = isOnline
    ? authData?.isAuthenticated
    : offlineData?.isAuthenticated;

  const { role_code } =
    (isOnline ? authData?.user : offlineUserData?.user) ?? {};

  const is_trial = isOnline
    ? authData?.is_trial
    : offlineUserData?.is_trial ?? {};

  const permissions = useSelector(selectPermissions);
  const permissionsError = useSelector(selectPermissionsError);

  const landingRoute = resolveLandingRoute(
    permissions,
    role_code,
    is_trial,
    isOnline
  );

  const isAllowedHotel =
    authData?.hotel?.hotel_key === "fsVCL7GK8n4dqHTVGHojPv";

  const conditionalRoutes =
    isAllowedHotel ||
      process.env.REACT_APP_BASE_URL !== "https://api.guesthives.com/"
      ? [
        {
          path: "customer-support",
          element: (
            <PermissonGuard>
              {SuspenseFn(<CustomerSupport />)}
            </PermissonGuard>
          ),
        },
        {
          path: "employee-management",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Employee />)}
            </PermissonGuard>
          ),
        },
        {
          path: "employee/task",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Employee emp="emptask" />)}
            </PermissonGuard>
          ),
        },
        {
          path: "employee/shift",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Employee emp="empshift" />)}
            </PermissonGuard>
          ),
        },
        {
          path: "employee/attendance",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Employee emp="attendance" />)}
            </PermissonGuard>
          ),
        },
        //      {
        //   path: "cctv",
        //   element: (
        //     <PermissonGuard>
        //       {SuspenseFn(<Cctv />)}
        //     </PermissonGuard>
        //   ),
        // },
        {
          path: "revenue-management/dynamic-pricing",
          element: (
            <PermissonGuard>
              {SuspenseFn(<DynamicPricing />)}
            </PermissonGuard>
          ),
        },
        {
          path: "revenue-management/rules/:entity",
          element: (
            <PermissonGuard>
              {SuspenseFn(<AddRule />)}
            </PermissonGuard>
          ),
        },
        {
          path: "revenue-management/channel-performance",
          element: (
            <PermissonGuard>
              {SuspenseFn(<ChannelPerformance />)}
            </PermissonGuard>
          ),
        },
        {
          path: "settings/rate-plan",
          element: (
            <PermissonGuard>
              {SuspenseFn(<RatePlan />)}
            </PermissonGuard>
          ),
        },
        {
          path: "settings/booking-engine",
          element: (
            <PermissonGuard>
              {SuspenseFn(<BookingEngineConfig />)}
            </PermissonGuard>
          ),
        },
      ]
      : [];

  const LandingRedirect = () => {
    if (isOnline && !isAuthenticated) {
      return <Navigate to="/auth/login" replace />;
    }
    return isLandingRouteResolving(
      permissions,
      role_code,
      isOnline,
      permissionsError
    ) ? null : (
      <Navigate to={landingRoute} replace />
    );
  };

  const Auth = ({ children }) => {
    if (isAuthenticated) {
      return <LandingRedirect />;
    }
    return <>{children}</>;
  };

  let routes = useRoutes([
    {
      path: "/",
      element: <LandingRedirect />,
    },
    {
      path: "/auth",
      children: [
        {
          path: "",
          element: <Navigate to="login" replace />,
        },
        {
          path: "login",
          element: (
            <Auth>
              <Login />
            </Auth>
          ),
        },
        {
          // path: "register",
          // element: <Register />,
        },
      ],
    },
    {
      path: "/set-password",
      element: <AuthGuard>{SuspenseFn(<SetPassword />)}</AuthGuard>,
    },
    {
      path: "/main",
      element: (
        <AuthGuard>
          <HotelGuard>
            {SuspenseFn(
              <MainLayout
                toggle={toggle}
                setToggle={setToggle}
                isMobile={isMobile}
              />
            )}
          </HotelGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: "",
          element: <LandingRedirect />,
        },
        {
          path: "dashboard",
          element: (
            <AuthGuard>
              <PermissonGuard>
                {SuspenseFn(<ReceptionDashboard />)}
              </PermissonGuard>
            </AuthGuard>
          ),
        },
        {
          path: "guestinfo/:table_type?",
          element: (
            <PermissonGuard>
              {SuspenseFn(<GuestInfo />)}
            </PermissonGuard>
          ),
        },
        {
          path: "asset-management",
          element: (
            <PermissonGuard>
              {SuspenseFn(<AssetReport />)}
            </PermissonGuard>
          ),
        },
        // {
        //   path: "employee-management",
        //   element: (
        //     <PermissonGuard>
        //       {SuspenseFn(<Employee />)}
        //     </PermissonGuard>
        //   ),
        // },
        {
          path: "facilitysettings",
          element: (
            <PermissonGuard>
              {SuspenseFn(<RoomSetting />)}
            </PermissonGuard>
          ),
        },
        {
          path: "notification",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Notification />)}
            </PermissonGuard>
          ),
        },
        {
          path: "facilitysettings/addroomtype",
          element: (
            <PermissonGuard>
              {SuspenseFn(<RoomTypeForm />)}
            </PermissonGuard>
          ),
        },
        {
          path: "facilitysettings/floorview",
          element: (
            <PermissonGuard>
              {SuspenseFn(<SyncFloorRoom />)}
            </PermissonGuard>
          ),
        },
        {
          path: "setup",
          element: (
            <PermissonGuard
              isSetup={true}
            >
              {SuspenseFn(
                <HotelSetupStepper
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "cctv-devices",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Cctv />)}
            </PermissonGuard>
          ),
        },
        {
          path: "sales-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <SalesReports
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "guest-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <GuestReport
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "night-audit-process",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <NightAuditProcess
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "night-audit-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <NightAuditReport
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "night-audit-operational-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <NightAuditOperationalReport
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "gst-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <GstReports
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "foreigner-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <ForeignerReports
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "occupancy-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <OccupancyReports
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "cancellation-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <CancellationReports
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "collection-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <CollectionReports
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "housekeeping-report",
          element: (
            <PermissonGuard>
              {SuspenseFn(
                <HousekeepingReports
                  toggle={toggle}
                  setToggle={setToggle}
                  isMobile={isMobile}
                />
              )}
            </PermissonGuard>
          ),
        },
        {
          path: "reservations",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Reservation />)}
            </PermissonGuard>
          ),
        },
        {
          path: "reservation/:entity?",
          element: (
            <PermissonGuard>
              {SuspenseFn(<NewReservation />)}
            </PermissonGuard>
          ),
        },
        {
          path: "arrival/:entity?",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Arrival />)}
            </PermissonGuard>
          ),
        },
        {
          path: "departure/:entity?",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Departure />)}
            </PermissonGuard>
          ),
        },
        {
          path: "updateprofile",
          element: (
            <PermissonGuard>
              {SuspenseFn(<UpdateProfile />)}
            </PermissonGuard>
          ),
        },
        {
          path: "customer-charges/charges-postings",
          element: (
            <PermissonGuard>
              {SuspenseFn(<ChargePosting />)}
            </PermissonGuard>
          ),
        },
        {
          path: "customer-charges/charges-posting/:key?",
          element: (
            <PermissonGuard>
              {SuspenseFn(<NewChargePosting />)}
            </PermissonGuard>
          ),
        },
        {
          path: "customer-charges/guest-disbursement",
          element: (
            <PermissonGuard>
              {SuspenseFn(<GuestDisbursementTable />)}
            </PermissonGuard>
          ),
        },
        {
          path: "customer-charges/paid-out",
          element: (
            <PermissonGuard>
              {SuspenseFn(<PaidOutTable />)}
            </PermissonGuard>
          ),
        },
        {
          path: "housekeeping",
          element: (
            <PermissonGuard
            >
              {SuspenseFn(<HouseKeepingEmployees />)}
            </PermissonGuard>
          ),
        },
        {
          path: "housekeeping-tasks",
          element: (
            <PermissonGuard
            >
              {SuspenseFn(<HouseKeepingTasks />)}
            </PermissonGuard>
          ),
        },
        {
          path: "customer-support",
          element: (
            <AuthGuard>
              <PermissonGuard>
                {SuspenseFn(<CustomerSupport />)}
              </PermissonGuard>
            </AuthGuard>
          ),
        },
        // {
        //   path: "revenue-management/dynamic-pricing",
        //   element: (
        //     <AuthGuard>
        //       <PermissonGuard>
        //         {SuspenseFn(<DynamicPricing />)}
        //       </PermissonGuard>
        //     </AuthGuard>
        //   ),
        // },
        // {
        //   path: "revenue-management/rules/:entity",
        //   element: (
        //     <AuthGuard>
        //       <PermissonGuard>
        //         {SuspenseFn(<AddRule />)}
        //       </PermissonGuard>
        //     </AuthGuard>
        //   ),
        // },

        {
          path: "channel-management/rate-update",
          element: (
            <PermissonGuard>
              {SuspenseFn(<RateUpdate />)}
            </PermissonGuard>
          ),
        },
        {
          path: "channel-management/room-update",
          element: (
            <PermissonGuard>
              {SuspenseFn(<UpdateRooms />)}
            </PermissonGuard>
          ),
        },
        {
          path: "channel-management/restrictions",
          element: (
            <PermissonGuard>
              {SuspenseFn(<Restrictions />)}
            </PermissonGuard>
          ),
        },
        {
          path: "tutorial-view",
          element: (
            <PermissonGuard>
              {SuspenseFn(<TutorialView />)}
            </PermissonGuard>
          ),
        },
        {
          path: "coupons",
          element: <Navigate to="/main/coupons/configuration" replace />,
        },
        {
          path: "coupons/configuration",
          element: (
            <PermissonGuard>
              {SuspenseFn(<CouponsPage />)}
            </PermissonGuard>
          ),
        },
        {
          path: "coupons/guest-offers",
          element: (
            <PermissonGuard>
              {SuspenseFn(<GuestOffersPage toggle={toggle} />)}
            </PermissonGuard>
          ),
        },
        {
          path: "user-management",
          element: (
            <PermissonGuard>
              {SuspenseFn(<UserManagement />)}
            </PermissonGuard>
          ),
        },
        {
          path: "user-management/create",
          element: (
            <PermissonGuard>
              {SuspenseFn(<UserForm />)}
            </PermissonGuard>
          ),
        },
        {
          path: "user-management/:userKey",
          element: (
            <PermissonGuard>
              {SuspenseFn(<UserView />)}
            </PermissonGuard>
          ),
        },
        {
          path: "role-management",
          element: (
            <PermissonGuard>
              {SuspenseFn(<RoleManagement />)}
            </PermissonGuard>
          ),
        },
        {
          path: "role-management/create",
          element: (
            <PermissonGuard>
              {SuspenseFn(<RoleForm />)}
            </PermissonGuard>
          ),
        },
        {
          path: "role-management/:roleKey",
          element: (
            <PermissonGuard>
              {SuspenseFn(<RoleView />)}
            </PermissonGuard>
          ),
        },
        {
          path: "audit-management",
          element: (
            <PermissonGuard>
              {SuspenseFn(<AuditManagement />)}
            </PermissonGuard>
          ),
        },
        {
          path: "transaction-types",
          element: (
            <PermissonGuard>
              {SuspenseFn(<TransactionTypes />)}
            </PermissonGuard>
          ),
        },
        {
          path: "approval-queue",
          element: (
            <PermissonGuard>
              {SuspenseFn(<ApprovalQueue />)}
            </PermissonGuard>
          ),
        },
        ...conditionalRoutes,

      ],
    },

    {
      path: "/admin",
      element: (
        <AuthGuard>
          <PermissonGuard isadminDashboard={true}>
            {SuspenseFn(
              <AdminDashboard
                toggle={toggle}
                setToggle={setToggle}
                isMobile={isMobile}
              />
            )}
          </PermissonGuard>
        </AuthGuard>
      ),
    },
    {
      path: "/tutorial-management",
      element: (
        <AuthGuard>
          <PermissonGuard>
            {SuspenseFn(
              <MainLayout
                toggle={toggle}
                setToggle={setToggle}
                isMobile={isMobile}
              />
            )}
          </PermissonGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: "",
          element: SuspenseFn(<TutorialManagement />),
        },
      ],
    },
    {
      path: "gmail-auth",
      element: <GmailAuth />,
    },
    {
      path: "access-denied",
      element: <AccessDenied />,
    },
    {
      path: "*",
      element: <PageNotFound />,
    },
    {
      path: "selfcheckin",
      element: SuspenseFn(
        <SelfCheckIn
          toggle={toggle}
          setToggle={setToggle}
          isMobile={isMobile}
        />
      ),
    },
    {
      path: "selfupdate",
      element: SuspenseFn(
        <SelfUpdate />
      ),
    },
  ]);

  const offlineRoutes = useRoutes([
    {
      path: "/",
      element: <Navigate to={landingRoute} replace />,
    },
    {
      path: "/auth",
      children: [
        {
          path: "",
          element: <Navigate to="login" replace />,
        },
        {
          path: "login",
          element: (
            <Auth>
              <Login />
            </Auth>
          ),
        },
      ],
    },
    {
      path: "/main",
      element: SuspenseFn(
        <AuthGuard>
          <PermissonGuard>
            <MainLayout
              toggle={toggle}
              setToggle={setToggle}
              isMobile={isMobile}
            />
          </PermissonGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: "offline-arrival",
          element: SuspenseFn(<OfflineArrival />),
        },
        {
          path: "offline",
          element: SuspenseFn(<OfflineBanner />),
        },
      ],
    },
    {
      path: "offline",
      element: SuspenseFn(<OfflineBanner />),
    },
    {
      path: "*",
      element: <PageNotFound />,
    },
  ]);

  return <>{!isOnline ? offlineRoutes : routes}</>;
}

export default BaseRoutes;
